import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "377",
  "slug": "what-is-ndvi-satellite-vegetation",
  "title": "What Is NDVI? How Satellites Measure the Health of Plants From Space",
  "excerpt": "NDVI is the simple index that lets satellites map crops, forests, and droughts from orbit. Here's how it works and why it's everywhere in modern geography.",
  "date": "2026-06-08",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1514864151880-d1bef4892f29?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxncmVlbiUyMGZhcm1sYW5kJTIwYWVyaWFsfGVufDF8MHx8fDE3ODA5MDYyODh8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "NDVI (the Normalized Difference Vegetation Index) compares how much near-infrared and red light a surface reflects to measure how green and healthy plants are. Healthy vegetation scores high; bare soil, rock, and water score low. It's one of the most widely used tools in satellite imagery.",
  "content": [
    {
      "type": "paragraph",
      "text": "When you hear that satellites can 'see' crops failing, forests thinning, or a drought spreading, there's a good chance the tool behind the headline is NDVI — the Normalized Difference Vegetation Index. It sounds technical, but the idea is simple and surprisingly elegant, and once you understand it you'll start noticing it everywhere in maps and the news."
    },
    {
      "type": "heading",
      "text": "The Trick: Plants Love Infrared"
    },
    {
      "type": "paragraph",
      "text": "Healthy green plants do something specific with light. Their leaves absorb most of the red light that hits them, because that's the light chlorophyll uses for photosynthesis. At the same time, the internal structure of a healthy leaf reflects a large amount of near-infrared light — a part of the spectrum our eyes can't see, but satellites can."
    },
    {
      "type": "paragraph",
      "text": "So a thriving plant is dark in red light and bright in near-infrared. A stressed, dying, or dead plant reflects more red and less infrared. Bare soil, rock, and water behave differently again. NDVI turns that contrast into a single number."
    },
    {
      "type": "heading",
      "text": "The Formula, in Plain English"
    },
    {
      "type": "paragraph",
      "text": "NDVI is calculated by taking the difference between the near-infrared and red reflectance, then dividing by their sum. Written out, it's (NIR minus Red) divided by (NIR plus Red). The result always falls between -1 and +1, which makes different images easy to compare."
    },
    {
      "type": "list",
      "items": [
        "Values from about 0.6 to 0.9 indicate dense, healthy vegetation — rainforests, lush crops, thriving grassland.",
        "Values around 0.2 to 0.4 suggest sparse or stressed vegetation — shrubland, dry grass, struggling fields.",
        "Values near 0 indicate bare ground, rock, sand, or built-up areas.",
        "Negative values usually mean water, snow, or cloud."
      ]
    },
    {
      "type": "paragraph",
      "text": "Because the calculation divides by the total light, it partly cancels out differences in brightness — like shadows or varying sun angles — which is why such a simple ratio turns out to be so robust."
    },
    {
      "type": "heading",
      "text": "What People Use It For"
    },
    {
      "type": "paragraph",
      "text": "NDVI is one of the workhorses of modern remote sensing. Because the same satellites pass over the same places again and again, you can watch the index change over time and learn a great deal:"
    },
    {
      "type": "list",
      "items": [
        "Agriculture: tracking crop health field by field and spotting trouble before it's visible from the ground.",
        "Drought monitoring: watching vegetation 'green up' in the wet season and brown off when rain fails.",
        "Deforestation: detecting where forest is being cleared as NDVI suddenly drops.",
        "Wildfire recovery: measuring how quickly burned land regrows over the following seasons.",
        "Urban planning: mapping green space and the cooling effect of vegetation in cities."
      ]
    },
    {
      "type": "heading",
      "text": "Seeing the Invisible"
    },
    {
      "type": "paragraph",
      "text": "What makes NDVI powerful is that it reveals information our eyes can't pick up directly. Two fields might look similarly green to a passing driver, but their NDVI values can be very different — one vigorous, one quietly stressed. Satellites like Landsat and the Sentinel series capture the necessary infrared bands every few days, so this 'invisible' health check happens continuously across the whole planet."
    },
    {
      "type": "heading",
      "text": "Where the Idea Came From"
    },
    {
      "type": "paragraph",
      "text": "NDVI isn't new. Researchers developed it in the 1970s using data from the early Landsat satellites, looking for a simple way to pull a vegetation signal out of noisy imagery. Its staying power comes from that simplicity: it needs only two bands of light and a bit of arithmetic, yet it works across deserts, farms, and rainforests alike. Half a century later it's still one of the first tools an analyst reaches for."
    },
    {
      "type": "heading",
      "text": "What It Can't Do"
    },
    {
      "type": "paragraph",
      "text": "NDVI has real limits worth knowing. In very dense forest it 'saturates' — the value maxes out and can't tell a healthy canopy from an extraordinary one. In sparsely vegetated areas, the colour of the bare soil underneath can skew the reading. And like any optical measurement, clouds and haze get in the way. Analysts often pair it with other indices to fill these gaps."
    },
    {
      "type": "heading",
      "text": "Why It's Worth Knowing"
    },
    {
      "type": "paragraph",
      "text": "Understanding NDVI changes how you read a false-colour satellite image. Those striking maps where vegetation glows bright red are often using the near-infrared band that NDVI relies on. Once you know plants are infrared-bright, the colours stop being arbitrary and start telling a story about what's alive and what isn't."
    }
  ]
};
